import React, { PropsWithChildren } from 'react';

import { Box, PancakeStack } from '@betterment/samba-react';
import { vstack } from '@betterment/samba-styled-system/patterns';
import { SystemStyleObject } from '@betterment/samba-styled-system/types';

type PropsWithCss = PropsWithChildren<{
  css?: SystemStyleObject;
}>;

const WorkflowPageContainer: React.FC<PropsWithCss> = ({ css, children }) => {
  return (
    <PancakeStack
      css={{
        bg: 'background.surface',
        maxWidth: '100%',
        maxHeight: '100vh',
        overflow: 'hidden',
        ...css,
      }}
    >
      {children}
    </PancakeStack>
  );
};

const Main: React.FC<PropsWithCss> = ({ css, children }) => {
  return (
    <PancakeStack.Main
      css={{
        bg: 'background.surface',
        maxWidth: '100%',
        maxHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <div
        className={vstack({
          gap: '0',
          width: '100%',
          py: 'space.600',
          px: '104px',
          alignItems: 'flex-start',
          maxHeight: '100%',
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          ...css,
        })}
      >
        {children}
      </div>
    </PancakeStack.Main>
  );
};

const Footer: React.FC<PropsWithCss> = ({ css, children }) => {
  return (
    <PancakeStack.Footer
      css={{
        borderColor: 'border',
        borderTop: 'solid 1px',
        maxWidth: '100%',
        px: '104px',
        position: 'sticky',
        bottom: '0',
        background: 'background.surface',
        overflowY: 'hidden',
        scrollbarGutter: 'stable',
      }}
    >
      <Box
        css={{
          display: 'flex',
          spaceX: 'space.200',
          xl: {
            maxWidth: 'calc(100vw - 208px)',
          },
          minHeight: '88px',
          alignItems: 'center',
          mx: 'auto',
          height: '100%',
          ...css,
        }}
      >
        {children}
      </Box>
    </PancakeStack.Footer>
  );
};

const Header: React.FC<PropsWithCss> = ({ css, children }) => {
  return (
    <PancakeStack.Header
      css={{
        borderBottom: 'solid 1px',
        borderColor: 'border',
        px: 'space.200',
        maxWidth: '100%',
        position: 'sticky',
        top: '0',
        background: 'background.surface',
      }}
    >
      <Box
        css={{
          xl: {
            maxWidth: 'calc(100vw - 208px)',
          },
          height: '80px',
          display: 'grid',
          gridTemplateColumns: '1fr 2fr 1fr',
          alignItems: 'center',
          mx: 'auto',
          ...css,
        }}
      >
        {children}
      </Box>
    </PancakeStack.Header>
  );
};

type CompositeComponent = typeof WorkflowPageContainer & {
  Header: typeof Header;
  Main: typeof Main;
  Footer: typeof Footer;
};

const WorkflowPage = WorkflowPageContainer as CompositeComponent;
WorkflowPage.displayName = 'WorkflowPage';
WorkflowPage.Header = Header;
WorkflowPage.Header.displayName = 'WorkflowPage.Header';
WorkflowPage.Main = Main;
WorkflowPage.Main.displayName = 'WorkflowPage.Main';
WorkflowPage.Footer = Footer;
WorkflowPage.Footer.displayName = 'WorkflowPage.Footer';

export { WorkflowPage };
